// Чистата логика на справката „Доставки": филтрите в колоните (Тип ·
// Доставчик · Склад · Въвел · Фактура · Бруто от–до · Плащане), търсенето,
// сортирането и KPI. Броячите в попъпите на колоните са фасетни (виж
// `components/filters/columnFilterModel.ts`): смятат се по периода след
// търсенето и ОСТАНАЛИТЕ филтри — изборът на един доставчик не нулира броя
// до останалите.
import type { Delivery } from "../../../../api/inventory";
import {
  deliveryGrossOf,
  deliveryNetOf,
  docTypeOf,
  isDeliveryAnnulled,
  noteInvoiceStateOf,
  payKeyOf,
  warehouseNamesOf,
  type DeliveryPayKey,
} from "../../delivery/components/deliveryMeta";
import {
  EMPTY_RANGE,
  type FilterSpecs,
  type NumberRange,
} from "../../components/filters/columnFilterModel";

export type DeliveryFilters = {
  types: string[]; // invoice | note
  suppliers: string[];
  warehouses: string[];
  users: string[];
  // стоковите спрямо фактурата: waiting | closed | inInvoice (фактурите — не са опция)
  invoiceStates: string[];
  gross: NumberRange;
  pays: string[]; // DeliveryPayKey
};

export const EMPTY_DELIVERY_FILTERS: DeliveryFilters = {
  types: [],
  suppliers: [],
  warehouses: [],
  users: [],
  invoiceStates: [],
  gross: EMPTY_RANGE,
  pays: [],
};

export type DeliveryFilterKey = keyof DeliveryFilters;

export type DeliverySortKey = "date" | "doc" | "supplier" | "sum";
export type DeliverySort = { key: DeliverySortKey; dir: "asc" | "desc" };

export const supplierOf = (delivery: Delivery) => delivery.supplierName || "—";
export const userOf = (delivery: Delivery) => delivery.createdByName || "—";

export const PAY_KEYS: DeliveryPayKey[] = ["unpaid", "paid", "byinv", "annulled"];
export const INVOICE_STATE_KEYS = ["waiting", "closed", "inInvoice"] as const;
// Стойността на фактурите във филтъра „Фактура" — не е опция
const NOT_A_NOTE = "—";

// Етикетите идват от страницата (i18n) — моделът остава чист
export type DeliveryFilterLabels = {
  titles: Record<DeliveryFilterKey, string>;
  typeLabel: (value: string) => string;
  invoiceStateLabel: (value: string) => string;
  payLabel: (value: string) => string;
  formatMoney: (value: number) => string;
};

export const deliveryFilterSpecs = ({
  titles,
  typeLabel,
  invoiceStateLabel,
  payLabel,
  formatMoney,
}: DeliveryFilterLabels): FilterSpecs<Delivery, DeliveryFilters> => ({
  types: {
    kind: "list",
    title: titles.types,
    valuesOf: (delivery) => [docTypeOf(delivery)],
    labelOf: typeLabel,
    order: ["invoice", "note"],
  },
  suppliers: { kind: "list", title: titles.suppliers, valuesOf: (delivery) => [supplierOf(delivery)], searchable: true },
  warehouses: { kind: "list", title: titles.warehouses, valuesOf: warehouseNamesOf },
  users: { kind: "list", title: titles.users, valuesOf: (delivery) => [userOf(delivery)] },
  invoiceStates: {
    kind: "list",
    title: titles.invoiceStates,
    valuesOf: (delivery) => [noteInvoiceStateOf(delivery) ?? NOT_A_NOTE],
    labelOf: invoiceStateLabel,
    order: INVOICE_STATE_KEYS,
    skipValue: NOT_A_NOTE,
  },
  gross: { kind: "range", title: titles.gross, valueOf: deliveryGrossOf, format: formatMoney },
  // Заварените документи отпреди плащането („без данни") не са опция
  pays: {
    kind: "list",
    title: titles.pays,
    valuesOf: (delivery) => [payKeyOf(delivery)],
    labelOf: payLabel,
    order: PAY_KEYS,
    skipValue: "untracked",
  },
});

const matchesQuery = (delivery: Delivery, words: string[]) => {
  if (!words.length) return true;
  const haystack = [
    delivery.invoiceNumber ?? "",
    `№${delivery.id}`,
    supplierOf(delivery),
    userOf(delivery),
    delivery.note ?? "",
    ...delivery.lines.map((line) => line.productName),
  ]
    .join(" ")
    .toLowerCase();
  return words.every((word) => haystack.includes(word));
};

// Търсачката над таблицата — базата, върху която работят филтрите в колоните
export const searchDeliveries = (deliveries: Delivery[], query: string) => {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  return words.length ? deliveries.filter((delivery) => matchesQuery(delivery, words)) : deliveries;
};

export const sortDeliveries = (deliveries: Delivery[], sort: DeliverySort) => {
  const sign = sort.dir === "asc" ? 1 : -1;
  const compare = (a: Delivery, b: Delivery) => {
    switch (sort.key) {
      case "date":
        return a.date.localeCompare(b.date);
      case "doc":
        return (a.invoiceNumber ?? "").localeCompare(b.invoiceNumber ?? "", "bg", {
          numeric: true,
        });
      case "supplier":
        return supplierOf(a).localeCompare(supplierOf(b), "bg");
      case "sum":
        return deliveryGrossOf(a) - deliveryGrossOf(b);
    }
  };
  return [...deliveries].sort((a, b) => compare(a, b) * sign || b.id - a.id);
};

// Стокова, обобщена от фактура, е вътре в сумата на фактурата → не се брои пак
const countsInSum = (delivery: Delivery) =>
  !isDeliveryAnnulled(delivery) && !(docTypeOf(delivery) === "note" && delivery.invoiceId != null);

export const sumGross = (deliveries: Delivery[]) =>
  deliveries.filter(countsInSum).reduce((sum, d) => sum + deliveryGrossOf(d), 0);
export const sumNet = (deliveries: Delivery[]) =>
  deliveries.filter(countsInSum).reduce((sum, d) => sum + deliveryNetOf(d), 0);

export const deliveryKpis = (deliveries: Delivery[]) => {
  const active = deliveries.filter((delivery) => !isDeliveryAnnulled(delivery));
  const payKnown = active.some((delivery) => payKeyOf(delivery) !== "untracked");
  const typeKnown = active.some((delivery) => delivery.documentType != null);
  return {
    count: active.length,
    gross: sumGross(active),
    unpaid: active
      .filter((delivery) => payKeyOf(delivery) === "unpaid")
      .reduce((sum, d) => sum + deliveryGrossOf(d), 0),
    // само чакащите фактура (приключените без фактура не се броят)
    uninvoicedNotes: active.filter((delivery) => noteInvoiceStateOf(delivery) === "waiting").length,
    payKnown,
    typeKnown,
  };
};
